import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, Send, Save, Star, PoundSterling, ClipboardList } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { PROPERTY_TYPES, WINDOW_TYPES, MATERIALS, LOUVRE_SIZES } from './constants';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateSurveyFromEstimate = ({ estimate, open, onOpenChange, onCreated }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [customTotalPrice, setCustomTotalPrice] = useState(null);
  
  // Initialize form data from estimate
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_postcode: '',
    customer_address: '',
    survey_date: new Date().toISOString().slice(0, 16),
    surveyor_name: '',
    property_type: 'house',
    property_notes: '',
    material: 'paulownia',
    hinge_colour: 'White',
    openings: [],
    general_notes: '',
    access_notes: '',
    installation_notes: '',
    customer_notes: ''
  });

  // Populate form from estimate when opened
  useEffect(() => {
    if (estimate && open) {
      const material = estimate.material?.toLowerCase() || 'paulownia';
      const materialId = MATERIALS.find(m => 
        m.name.toLowerCase().includes(material) || 
        m.id === material
      )?.id || 'paulownia';

      // Convert estimate windows to survey openings
      const openings = (estimate.windows || []).map((window, index) => ({
        room_location: window.label || `Window ${index + 1}`,
        window_type: 'standard',
        layout: '',
        width: window.width || 0,
        height: window.height || 0,
        depth: 100,
        midrail: null,
        split_height: null,
        louvre_size: '89mm',
        colour: 'White',
        material: materialId,
        notes: ''
      }));

      setFormData(prev => ({
        ...prev,
        customer_name: estimate.customer_name || '',
        customer_email: estimate.customer_email || '',
        customer_phone: estimate.customer_phone || '',
        customer_postcode: estimate.customer_postcode || '',
        material: materialId,
        openings: openings,
        general_notes: `Created from estimate. Style: ${estimate.shutter_style || 'N/A'}, Material: ${estimate.material || 'N/A'}`
      }));
      
      setCustomTotalPrice(estimate.total_cost || null);
      setStep(1);
    }
  }, [estimate, open]);

  // Calculate live pricing
  const pricingData = useMemo(() => {
    let totalSqm = 0;
    let calculatedTotalPrice = 0;
    
    const windowPrices = formData.openings.map((opening, index) => {
      const openingMaterial = MATERIALS.find(m => m.id === opening.material) || 
                              MATERIALS.find(m => m.id === formData.material) || 
                              MATERIALS.find(m => m.id === 'paulownia');
      const pricePerSqm = openingMaterial?.pricePerSqm || 300;
      
      const width = parseFloat(opening.width) || 0;
      const height = parseFloat(opening.height) || 0;
      const sqm = (width * height) / 1000000;
      const price = sqm * pricePerSqm;
      
      totalSqm += sqm;
      calculatedTotalPrice += price;
      
      return {
        index,
        label: opening.room_location || `Opening ${index + 1}`,
        layout_code: opening.layout || 'N/A',
        width: width,
        height: height,
        sqm: sqm.toFixed(2),
        calculatedPrice: Math.round(price * 100) / 100,
        price: Math.round(price * 100) / 100,
        material: openingMaterial?.name || 'Paulownia Timber',
        materialId: openingMaterial?.id || 'paulownia',
        pricePerSqm: pricePerSqm
      };
    });
    
    const finalTotalPrice = customTotalPrice !== null ? customTotalPrice : calculatedTotalPrice;
    const priceRatio = calculatedTotalPrice > 0 ? finalTotalPrice / calculatedTotalPrice : 1;
    
    const adjustedWindowPrices = windowPrices.map(w => ({
      ...w,
      price: Math.round(w.calculatedPrice * priceRatio * 100) / 100
    }));
    
    const depositAmount = finalTotalPrice * 0.6;
    const balanceAmount = finalTotalPrice * 0.4;
    const materialsUsed = [...new Set(windowPrices.map(w => w.material))];
    
    return {
      materialsUsed,
      totalSqm: totalSqm.toFixed(2),
      calculatedTotalPrice: Math.round(calculatedTotalPrice * 100) / 100,
      totalPrice: Math.round(finalTotalPrice * 100) / 100,
      depositAmount: Math.round(depositAmount * 100) / 100,
      balanceAmount: Math.round(balanceAmount * 100) / 100,
      windowPrices: adjustedWindowPrices,
      hasCustomPrice: customTotalPrice !== null,
      discountPercent: customTotalPrice !== null && calculatedTotalPrice > 0 
        ? Math.round((1 - (customTotalPrice / calculatedTotalPrice)) * 100) 
        : 0
    };
  }, [formData.material, formData.openings, customTotalPrice]);

  const resetForm = () => {
    setStep(1);
    setCustomTotalPrice(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_URL}/api/surveys/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast({
          title: "Survey Created",
          description: `Survey ${response.data.data.survey.survey_number} created from estimate`,
        });
        resetForm();
        onOpenChange(false);
        onCreated?.();
      } else {
        throw new Error(response.data.error || 'Failed to create survey');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create survey",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAndSendQuote = async () => {
    if (!formData.customer_email) {
      toast({
        title: "Email Required",
        description: "Customer email is required to send the quote",
        variant: "destructive",
      });
      return;
    }

    setSendingQuote(true);
    try {
      // First create the survey
      const token = localStorage.getItem('admin_token');
      const surveyResponse = await axios.post(`${API_URL}/api/surveys/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!surveyResponse.data.success) {
        throw new Error(surveyResponse.data.error || 'Failed to create survey');
      }

      // Then send the formal quote email
      const quotePayload = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_address: formData.customer_address,
        material: pricingData.materialsUsed.join(', '),
        louvre_size: formData.openings[0]?.louvre_size || '89mm',
        colour: formData.openings[0]?.colour || 'White',
        hinge_colour: formData.hinge_colour || 'White',
        windows: pricingData.windowPrices.map(w => ({
          label: w.label,
          layout_code: w.layout_code,
          price: w.price,
          material: w.material
        })),
        total_price: pricingData.totalPrice,
        customer_notes: formData.customer_notes || null
      };

      const quoteResponse = await axios.post(`${API_URL}/api/quotes/send-formal-quote`, quotePayload);

      if (quoteResponse.data.success) {
        toast({
          title: "Survey Created & Quote Sent!",
          description: `Quote sent to ${formData.customer_email}. Nurture campaign started.`,
        });
        resetForm();
        onOpenChange(false);
        onCreated?.();
      } else {
        throw new Error(quoteResponse.data.detail || 'Failed to send quote');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create survey and send quote",
        variant: "destructive",
      });
    } finally {
      setSendingQuote(false);
    }
  };

  const updateOpening = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      openings: prev.openings.map((o, i) => i === index ? { ...o, [field]: value } : o)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-[#8CB89F]" />
            Create Survey from Estimate
          </DialogTitle>
          <DialogDescription>
            Review and complete the survey details from the customer's estimate.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={`step-${step}`} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="step-1" onClick={() => setStep(1)}>1. Customer Info</TabsTrigger>
            <TabsTrigger value="step-2" onClick={() => setStep(2)}>2. Openings</TabsTrigger>
            <TabsTrigger value="step-3" onClick={() => setStep(3)}>3. Review & Send</TabsTrigger>
          </TabsList>

          {/* Step 1: Customer Info */}
          <TabsContent value="step-1" className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-4">
              <p className="text-sm text-blue-800">
                Pre-filled from estimate: {estimate?.shutter_style} shutters in {estimate?.material}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer_name">Customer Name *</Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_email">Email</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_phone">Phone</Label>
                <Input
                  id="customer_phone"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_postcode">Postcode</Label>
                <Input
                  id="customer_postcode"
                  value={formData.customer_postcode}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_postcode: e.target.value }))}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="customer_address">Address</Label>
                <Input
                  id="customer_address"
                  value={formData.customer_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                  placeholder="Full address (required for quote)"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="survey_date">Survey Date</Label>
                <Input
                  id="survey_date"
                  type="datetime-local"
                  value={formData.survey_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, survey_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="surveyor_name">Surveyor Name</Label>
                <Input
                  id="surveyor_name"
                  value={formData.surveyor_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, surveyor_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="property_type">Property Type</Label>
                <Select 
                  value={formData.property_type} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, property_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Material Selection */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-medium">Material Selection</Label>
              <div className="grid grid-cols-1 gap-2">
                {MATERIALS.map(material => (
                  <div
                    key={material.id}
                    onClick={() => setFormData(prev => ({ ...prev, material: material.id }))}
                    className={`cursor-pointer rounded-lg border-2 p-3 transition-all relative ${
                      formData.material === material.id 
                        ? 'border-[#8CB89F] bg-[#8CB89F]/5' 
                        : material.recommended 
                          ? 'border-amber-400 hover:border-amber-500 bg-amber-50/50'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {material.recommended && (
                      <div className="absolute -top-2 left-3 px-2 py-0.5 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3 fill-white" />
                        Recommended
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{material.name}</p>
                        <p className="text-sm text-gray-500">{material.description}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-[#2F4F4F]">£{material.pricePerSqm}</span>
                        <span className="text-xs text-gray-500 block">/m² inc VAT</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!formData.customer_name}>
                Next: Review Openings
              </Button>
            </div>
          </TabsContent>

          {/* Step 2: Openings */}
          <TabsContent value="step-2" className="space-y-4 mt-4">
            {/* Live Pricing Banner */}
            {formData.openings.length > 0 && (
              <div className="bg-gradient-to-r from-[#2F4F4F] to-[#3d6363] text-white p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/70">Live Quote Total</p>
                    <p className="text-2xl font-bold">£{pricingData.totalPrice.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-white/70">
                      {pricingData.materialsUsed.length === 1 
                        ? pricingData.materialsUsed[0]
                        : `${pricingData.materialsUsed.length} materials mixed`
                      }
                    </p>
                    <p className="text-sm text-white/70">{pricingData.totalSqm} m² total</p>
                  </div>
                </div>
              </div>
            )}

            {/* Openings List */}
            <div className="space-y-3">
              <Label>Openings from Estimate ({formData.openings.length})</Label>
              {formData.openings.map((opening, index) => {
                const windowPrice = pricingData.windowPrices[index];
                return (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border space-y-3">
                    <div className="flex justify-between items-center">
                      <div className="font-medium">{opening.room_location}</div>
                      <span className="font-bold text-[#2F4F4F]">
                        £{windowPrice?.price?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">Width (mm)</Label>
                        <Input
                          type="number"
                          value={opening.width}
                          onChange={(e) => updateOpening(index, 'width', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Height (mm)</Label>
                        <Input
                          type="number"
                          value={opening.height}
                          onChange={(e) => updateOpening(index, 'height', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Layout Code</Label>
                        <Input
                          value={opening.layout || ''}
                          onChange={(e) => updateOpening(index, 'layout', e.target.value)}
                          placeholder="e.g., 2-2"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Material</Label>
                        <Select 
                          value={opening.material} 
                          onValueChange={(v) => updateOpening(index, 'material', v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MATERIALS.map(m => (
                              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-500">
                      {windowPrice?.sqm || 0} m² @ £{windowPrice?.pricePerSqm}/m²
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={() => setStep(3)}>Next: Review & Send</Button>
            </div>
          </TabsContent>

          {/* Step 3: Review & Send */}
          <TabsContent value="step-3" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="customer_notes">Customer Notes (included in quote email)</Label>
                <Textarea
                  id="customer_notes"
                  value={formData.customer_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_notes: e.target.value }))}
                  placeholder="Notes to include in the customer's quote email..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="general_notes">Internal Notes (staff only)</Label>
                <Textarea
                  id="general_notes"
                  value={formData.general_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, general_notes: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>

            {/* Pricing Summary */}
            {formData.openings.length > 0 && (
              <div className="bg-gradient-to-br from-[#2F4F4F] to-[#3d6363] text-white p-5 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <PoundSterling className="w-5 h-5" />
                  <h4 className="font-semibold text-lg">Quote Summary</h4>
                </div>
                
                <div className="space-y-2 mb-4">
                  {pricingData.windowPrices.map((window, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <div>
                        <span className="text-white/80">{window.label}</span>
                        <span className="text-white/50 text-xs ml-2">({window.material})</span>
                      </div>
                      <span>£{window.price.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-white/20 pt-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold">Total Quote</span>
                    <div className="flex items-center gap-2">
                      <span className="text-white/70">£</span>
                      <Input
                        type="number"
                        value={customTotalPrice !== null ? customTotalPrice : pricingData.calculatedTotalPrice}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!isNaN(value) && value >= 0) {
                            setCustomTotalPrice(value);
                          } else if (e.target.value === '') {
                            setCustomTotalPrice(null);
                          }
                        }}
                        className="w-28 bg-white/10 border-white/30 text-white text-xl font-bold text-right"
                      />
                    </div>
                  </div>
                  
                  {pricingData.hasCustomPrice && (
                    <div className="flex justify-between text-sm">
                      <span className="text-yellow-300">
                        {pricingData.discountPercent > 0 ? `${pricingData.discountPercent}% discount applied` : 
                         pricingData.discountPercent < 0 ? `${Math.abs(pricingData.discountPercent)}% premium applied` : 
                         'Custom price set'}
                      </span>
                      <button 
                        onClick={() => setCustomTotalPrice(null)}
                        className="text-white/60 hover:text-white text-xs underline"
                      >
                        Reset to calculated
                      </button>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm text-white/70">
                    <span>Deposit (60%)</span>
                    <span>£{pricingData.depositAmount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm text-white/70">
                    <span>Balance on installation (40%)</span>
                    <span>£{pricingData.balanceAmount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-4">
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading || sendingQuote || !formData.customer_name}
                    variant="outline"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Survey Only
                  </Button>
                  <Button 
                    onClick={handleSubmitAndSendQuote} 
                    disabled={loading || sendingQuote || !formData.customer_name || !formData.customer_email}
                    className="bg-[#8CB89F] hover:bg-[#7aa88f]"
                  >
                    {sendingQuote ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Create Survey & Send Quote
                  </Button>
                </div>
              </div>
              {!formData.customer_email && (
                <p className="text-sm text-amber-600 text-right">
                  Add customer email to enable "Send Quote"
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSurveyFromEstimate;
